import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import type { ObjetivoVisita, Productor, TipoVisita } from '../../services/visitas.service';
import { RutaDiaStorageService } from '../../services/ruta-dia-storage.service';
import { ToastService } from '../../services/toast.service';
import {
  MAX_OBJETIVOS_VISITA_REGISTRO,
  normalizarIdsObjetivoVisita,
  objetivosAgendarValidators,
} from '../../utils/visita-registro.util';

function nombreCompleto(p: Productor): string {
  return [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean).join(' ').trim() || p.nombre || '';
}

@Component({
  selector: 'app-formulario-visita-offline',
  templateUrl: './formulario-visita-offline.component.html',
  styleUrls: ['./formulario-visita-offline.component.scss'],
  standalone: false,
})
export class FormularioVisitaOfflineComponent implements OnInit {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() guardado = new EventEmitter<void>();

  private readonly fb = inject(FormBuilder);
  private readonly storage = inject(RutaDiaStorageService);
  private readonly toast = inject(ToastService);

  readonly idEmergenciaInfo = 'formulario-visita-offline-emergencia-info';

  form: FormGroup = this.fb.group({
    id_productor: ['', Validators.required],
    lugar: ['', Validators.required],
    fecha_programada: ['', Validators.required],
    hora_programada: ['', Validators.required],
    id_tipo_visita: [null as number | null, Validators.required],
    id_objetivo_visita: [[] as number[], objetivosAgendarValidators()],
    bolsa_visita: [false],
    es_contingencia: [null as boolean | null, Validators.required],
    descripcion: ['', Validators.required],
    descripcion_emergencia: [''],
  });

  fechaProgramadaISO: string | null = null;
  horaProgramada: string | null = null;

  terminoBusqueda = '';
  productores: Productor[] = [];
  productoresFiltrados: Productor[] = [];
  tiposVisita: TipoVisita[] = [];
  objetivosVisita: ObjetivoVisita[] = [];
  readonly maxObjetivosVisitaRegistro = MAX_OBJETIVOS_VISITA_REGISTRO;
  guardando = false;
  /** Sin sync previo no hay catálogos locales para completar el formulario. */
  faltanCatalogosLocales = false;

  get productorSeleccionado(): Productor | null {
    const id = String(this.form.get('id_productor')?.value || '').trim();
    if (!id) return null;
    return this.productores.find(p => p.id_productor === id) ?? null;
  }

  async ngOnInit(): Promise<void> {
    this.productores = await this.storage.obtenerProductoresLocal();
    this.productoresFiltrados = this.productores.slice(0, 50);
    this.tiposVisita = await this.storage.obtenerTiposVisitaLocal();
    this.objetivosVisita = await this.storage.obtenerObjetivosVisitaLocal();
    this.faltanCatalogosLocales = this.productores.length === 0 || this.tiposVisita.length === 0;
    if (this.faltanCatalogosLocales) {
      await this.toast.mostrar(
        'No hay usuarios o tipos de visita en este dispositivo. Conéctese y use «Sincronizar Ruta del Día» en el dashboard.',
        'warning',
        4000
      );
    }
  }

  async onBuscarChange(): Promise<void> {
    this.productoresFiltrados = await this.storage.buscarProductoresLocal(this.terminoBusqueda, 50);
  }

  seleccionarProductor(p: Productor): void {
    this.form.patchValue({ id_productor: p.id_productor });
  }

  onFechaChange(ev: any): void {
    const v = ev?.detail?.value;
    this.fechaProgramadaISO = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : null);
    this.form.patchValue({ fecha_programada: this.fechaProgramadaISO || '' }, { emitEvent: true });
    this.form.get('fecha_programada')?.updateValueAndValidity({ emitEvent: true });
  }

  onHoraChange(ev: any): void {
    const v = ev?.detail?.value;
    const raw = typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : null);
    if (raw) {
      this.horaProgramada = this.extraerHoraHhMm(String(raw));
    } else {
      this.horaProgramada = '00:00';
    }
    this.form.patchValue({ hora_programada: this.horaProgramada || '' }, { emitEvent: true });
  }

  mostrarCampoDescripcionEmergencia(): boolean {
    return this.form.get('es_contingencia')?.value === true;
  }

  esFormularioValido(): boolean {
    if (this.form.invalid) return false;
    if (!this.fechaProgramadaISO) return false;
    if (!this.horaProgramada || String(this.horaProgramada).trim() === '') return false;
    const esEmergencia = this.form.get('es_contingencia')?.value === true;
    if (String(this.form.get('descripcion')?.value || '').trim() === '') return false;
    if (esEmergencia) {
      const desc = String(this.form.get('descripcion_emergencia')?.value || '').trim();
      if (!desc) return false;
    }
    return true;
  }

  /** ion-datetime puede devolver ISO completo o HH:mm; unificar a HH:mm. */
  private extraerHoraHhMm(valor: string): string {
    const raw = (valor ?? '').trim();
    if (!raw) return '00:00';
    if (raw.includes('T')) {
      const fecha = new Date(raw);
      if (Number.isNaN(fecha.getTime())) return '00:00';
      const horas = String(fecha.getHours()).padStart(2, '0');
      const minutos = String(fecha.getMinutes()).padStart(2, '0');
      return `${horas}:${minutos}`;
    }
    const m = raw.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return '00:00';
    return `${m[1].padStart(2, '0')}:${m[2]}`;
  }

  private formatearFechaHora(fechaISO: string, hora: string): string {
    const fecha = new Date(fechaISO);
    if (Number.isNaN(fecha.getTime())) {
      throw new Error('La fecha programada no es válida');
    }
    const hhmm = this.extraerHoraHhMm(hora || '00:00');
    const [horas, minutos] = hhmm.split(':');
    const h = parseInt(horas, 10);
    const min = parseInt(minutos, 10);
    if (!Number.isFinite(h) || !Number.isFinite(min) || h < 0 || h > 23 || min < 0 || min > 59) {
      throw new Error('La hora programada no es válida');
    }
    fecha.setHours(h, min, 0, 0);
    if (Number.isNaN(fecha.getTime())) {
      throw new Error('La fecha y hora programadas no son válidas');
    }
    return fecha.toISOString();
  }

  cerrar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  async guardar(): Promise<void> {
    if (!this.esFormularioValido()) {
      await this.toast.mostrar('Por favor complete todos los campos requeridos', 'warning', 2500);
      return;
    }
    const raw = this.form.getRawValue();
    const prod = this.productorSeleccionado;
    if (!prod) {
      await this.toast.mostrar('Seleccione un Usuario INDAP', 'warning', 2500);
      return;
    }
    const tipo = this.tiposVisita.find(t => t.id_tipo_visita === Number(raw.id_tipo_visita));
    const idObjetivosVisita = normalizarIdsObjetivoVisita(raw.id_objetivo_visita as number[] | null);
    const id = `offline-${Date.now()}`;
    const esContingencia = raw.es_contingencia === true;
    const descEmergencia = esContingencia
      ? String(raw.descripcion_emergencia ?? '').trim() || null
      : null;

    this.guardando = true;
    try {
      await this.storage.agregarPendienteSubidaDesdeAgendar({
        id,
        idProductor: prod.id_productor,
        nombreProductor: nombreCompleto(prod),
        subtituloTipo: tipo?.descripcion ?? '',
        fechaHoraIso: this.formatearFechaHora(this.fechaProgramadaISO!, this.horaProgramada || '00:00'),
        lugar: String(raw.lugar || '').trim(),
        idTipoVisita: Number(raw.id_tipo_visita),
        idObjetivosVisita: idObjetivosVisita.length ? idObjetivosVisita : undefined,
        bolsaVisita: raw.bolsa_visita === true,
        esContingencia,
        descripcionEmergencia: descEmergencia,
        descripcion: String(raw.descripcion ?? '').trim(),
        comentarios: '',
      });
      await this.toast.mostrar('Visita guardada offline. Quedó como “Pendiente de subir”.', 'success', 2500);
      this.guardado.emit();
      this.cerrar();
    } catch (e) {
      console.error(e);
      await this.toast.mostrar('No se pudo guardar la visita offline.', 'error', 3000);
    } finally {
      this.guardando = false;
    }
  }
}

